'use client';

import Image from 'next/image';
import * as React from 'react';
import { cn } from '@/lib/utils';

interface AvatarProps {
  imageUrl?: string;
  name?: string;
  variant?: 'square' | 'circle';
  width?: number;
  height?: number;
  className?: string;
  style?: React.CSSProperties;
}

const Avatar = React.forwardRef<HTMLImageElement, AvatarProps>(
  (
    {
      className,
      imageUrl,
      name,
      variant = 'circle',
      width = 300,
      height = 300,
      style,
    },
    ref
  ) => {
    const [isLoading, setIsLoading] = React.useState(true);
    const [hasError, setHasError] = React.useState(false);

    const formattedImageUrl =
      imageUrl && !imageUrl.startsWith('http') ? `https:${imageUrl}` : imageUrl;

    const showFallback = !formattedImageUrl || isLoading || hasError;

    return (
      <span
        className={cn(
          'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
          className
        )}
        style={style}
      >
        <Image
          ref={ref}
          src={showFallback ? '/images/avatar.svg' : formattedImageUrl!}
          alt={name ?? 'avatar'}
          width={width}
          height={height}
          className={cn(
            'object-cover',
            variant === 'circle' ? 'rounded-full' : 'rounded-md'
          )}
          sizes={`${width}px`}
          onLoadingComplete={() => setIsLoading(false)}
          onError={() => setHasError(true)}
          priority={showFallback}
        />
      </span>
    );
  }
);

Avatar.displayName = 'Avatar';

export default Avatar;
